import { Button, Segmented } from 'antd';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { ANALYTICS_DEFAULT_WINDOW, ANALYTICS_WINDOWS, type AnalyticsDto, type AnalyticsWindow } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { isWholeWindow, istTime } from '@/lib/analyticsReport';
import { queryKeys } from '@/lib/queryKeys';
import { USAGE_ANALYTICS_COPY } from './copy';
import { CoverageNotice } from './components/CoverageNotice';
import { GeographySection } from './components/GeographySection';
import { HealthSection } from './components/HealthSection';
import { LimitsNotice } from './components/LimitsNotice';
import { OfficerTrailPanel } from './components/OfficerTrailPanel';
import { OverviewSection } from './components/OverviewSection';
import { AnalyticsSection } from './components/AnalyticsSection';
import { SecuritySection } from './components/SecuritySection';
import { UsageHoursGrid } from './components/UsageHoursGrid';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  USAGE ANALYTICS — how PAMS is used, how the API answered, and what looked hostile.
 *  [ANALYTICS §5] SUPER_ADMIN, behind `analytics.read`.
 *
 *  ⚠️ ONE REQUEST PER WINDOW, AND THE WINDOW IS THE URL. `GET /analytics?window=` returns all five
 *  sections, each `{ data } | { failure }`, so a section the server could not compute renders its
 *  sentence and the other four still render. The window is `?window=` so a link opens the same
 *  view — and the DEFAULT is not written into the URL (`clearOnDefault`), the dashboard's rule that
 *  a shared link must not bake in whatever was the default the day it was copied.
 *
 *  ⚠️ `keepPreviousData`, SO SWITCHING WINDOWS KEEPS THE PAGE ON SCREEN while the next one loads —
 *  the subtitle says it is loading, so the numbers are never mistaken for the new window's.
 *
 *  ⚠️ ONLY THE 30-MINUTE WINDOW POLLS, EVERY MINUTE, AND NEVER IN THE BACKGROUND. It is read live
 *  from Redis; every longer window is built from rollups that change once a minute at most, so a
 *  poll there re-reads the same rows. `refetchIntervalInBackground` is banned by CLAUDE.md §8 and
 *  left at its default, so a hidden tab stops asking.
 *
 *  ⚠️ NO CENSUS OVERLAY. No `Tooltip`, `Dropdown`, `Drawer`, `Modal`, `Sheet` or `Dialog`: the
 *  caveats are sentences and the figures behind every graphic are a `<details>` table, so there
 *  is nothing here for `census.spec.ts` to sweep or exempt.
 *
 *  ⚠️ [ANALYTICS §6] AND ONE PANEL IS NOT PART OF THE REPORT. "Officer's trail" reads one
 *  officer's addresses and duty locations on a click, writes an audit row per look, and is the only
 *  place in the product that shows either — see `OfficerTrailPanel.tsx`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const LIVE_REFRESH_MS = 60_000;

const WINDOW_OPTIONS = ANALYTICS_WINDOWS.map((value) => ({ value, label: USAGE_ANALYTICS_COPY.windows[value] }));

/** The sections in page order, for the cold-start silhouettes. The data branch below names them again. */
const SECTIONS = [
  { id: 'overview', title: USAGE_ANALYTICS_COPY.overview.title },
  { id: 'usage-hours', title: USAGE_ANALYTICS_COPY.usageHours.title },
  { id: 'geography', title: USAGE_ANALYTICS_COPY.geography.title },
  { id: 'api-health', title: USAGE_ANALYTICS_COPY.health.title },
  { id: 'security', title: USAGE_ANALYTICS_COPY.security.title },
] as const;

const UsageAnalyticsPage = () => {
  const [analyticsWindow, setAnalyticsWindow] = useQueryState(
    'window',
    /* `parseAsStringLiteral` is the client half of the server's `z.enum` — `useServerTable`'s rule. */
    parseAsStringLiteral(ANALYTICS_WINDOWS)
      .withDefault(ANALYTICS_DEFAULT_WINDOW)
      .withOptions({ clearOnDefault: true, history: 'replace' }),
  );

  const report = useQuery({
    queryKey: queryKeys.analytics.view(analyticsWindow),
    queryFn: ({ signal }) => api.get<AnalyticsDto>('/analytics', { query: { window: analyticsWindow }, signal }),
    placeholderData: keepPreviousData,
    refetchInterval: analyticsWindow === '30m' ? LIVE_REFRESH_MS : false,
  });

  const data = report.data;
  const copy = USAGE_ANALYTICS_COPY;
  const subtitle =
    data === undefined
      ? undefined
      : report.isPlaceholderData
        ? copy.updating
        : report.isError
          ? copy.refreshFailed(istTime(data.generatedAt))
          : copy.updated(istTime(data.generatedAt));

  return (
    <div className="flex flex-col gap-6" data-testid="usage-analytics-root">
      <PageHeader
        icon={Activity}
        title={copy.title}
        subtitle={subtitle}
        actions={
          <Segmented<AnalyticsWindow>
            aria-label={copy.windowLabel}
            data-testid="analytics-window"
            value={analyticsWindow}
            options={WINDOW_OPTIONS}
            onChange={(value) => void setAnalyticsWindow(value)}
          />
        }
      />

      {data === undefined ? (
        report.isError ? (
          <EmptyState
            title={copy.errorTitle}
            description={copy.errorDescription}
            action={<Button onClick={() => void report.refetch()}>{copy.retry}</Button>}
          />
        ) : (
          /* Before the first report each section shows its own silhouette, under its own title. */
          SECTIONS.map((section) => (
            <AnalyticsSection key={section.id} id={section.id} title={section.title} state={undefined}>
              {() => null}
            </AnalyticsSection>
          ))
        )
      ) : (
        <>
          <CoverageNotice report={data} />
          <AnalyticsSection id="overview" title={copy.overview.title} state={data.overview}>
            {(section) => <OverviewSection data={section} grain={data.seriesGrain} />}
          </AnalyticsSection>
          <AnalyticsSection id="usage-hours" title={copy.usageHours.title} state={data.usageHours}>
            {(section) => (
              <UsageHoursGrid
                data={section}
                wholeWindow={isWholeWindow(data)}
                span={{ from: data.from, to: data.to }}
              />
            )}
          </AnalyticsSection>
          <AnalyticsSection id="geography" title={copy.geography.title} state={data.geography}>
            {(section) => <GeographySection data={section} />}
          </AnalyticsSection>
          <AnalyticsSection id="api-health" title={copy.health.title} state={data.apiHealth}>
            {(section) => <HealthSection data={section} grain={data.seriesGrain} />}
          </AnalyticsSection>
          <AnalyticsSection id="security" title={copy.security.title} state={data.security}>
            {(section) => <SecuritySection data={section} grain={data.seriesGrain} />}
          </AnalyticsSection>
        </>
      )}

      <LimitsNotice />

      {/* [ANALYTICS §6] Outside every report branch: the trail is its own audited read, not a section
          of the report, and it answers whatever the report did. */}
      <OfficerTrailPanel />
    </div>
  );
};

export default UsageAnalyticsPage;
